import { useEffect, useMemo, useState } from 'react';
import {
  Building2, Check, Clipboard, Clock3, Loader2, Plus, RefreshCw,
  ShieldCheck, UserCheck, UserX, Users, X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const roleLabels = { director: 'Директор', supervisor: 'Супервайзер', manager: 'Менеджер' };
const statusLabels = { pending: 'Ожидает', accepted: 'Принято', expired: 'Истекло', revoked: 'Отозвано' };

const OrganizationWorkspace = () => {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [network, setNetwork] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', role: 'manager', supervisorId: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [memberResponse, invitationResponse, networkResponse] = await Promise.all([
        api.get('/api/director/members'),
        api.get('/api/director/invitations'),
        api.get('/api/director/organization/network'),
      ]);
      setOrganization(memberResponse.data.organization);
      setMembers(memberResponse.data.members || []);
      setInvitations(invitationResponse.data.invitations || []);
      setNetwork(networkResponse.data.organizations || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось загрузить организацию');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const supervisors = useMemo(() => members.filter((member) => member.role === 'supervisor' && member.is_active), [members]);
  const activeManagers = members.filter((member) => member.role === 'manager' && member.is_active).length;
  const activeSupervisors = supervisors.length;
  const pendingManagers = invitations.filter((item) => item.role === 'manager' && item.status === 'pending').length;
  const pendingSupervisors = invitations.filter((item) => item.role === 'supervisor' && item.status === 'pending').length;

  const openInvite = () => {
    setGeneratedLink('');
    setForm({ email: '', firstName: '', lastName: '', role: 'manager', supervisorId: '' });
    setShowInvite(true);
  };

  const copyLink = async (link = generatedLink) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Ссылка скопирована');
    } catch {
      toast.error('Не удалось скопировать ссылку');
    }
  };

  const createInvitation = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await api.post('/api/director/invitations', {
        ...form,
        supervisorId: form.role === 'manager' && form.supervisorId ? form.supervisorId : null,
      });
      setGeneratedLink(response.data.invitation.invitationUrl);
      toast.success('Приглашение создано');
      const invitationsResponse = await api.get('/api/director/invitations');
      setInvitations(invitationsResponse.data.invitations || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось создать приглашение');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async (id) => {
    try {
      const response = await api.post(`/api/director/invitations/${id}/regenerate`);
      setGeneratedLink(response.data.invitation.invitationUrl);
      setShowInvite(true);
      await copyLink(response.data.invitation.invitationUrl);
      const list = await api.get('/api/director/invitations');
      setInvitations(list.data.invitations || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось обновить приглашение');
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/api/director/invitations/${id}`);
      toast.success('Приглашение отозвано');
      const list = await api.get('/api/director/invitations');
      setInvitations(list.data.invitations || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось отозвать приглашение');
    }
  };

  const toggleMember = async (member) => {
    try {
      await api.patch(`/api/director/members/${member.id}/toggle-active`);
      toast.success(member.is_active ? 'Сотрудник заблокирован' : 'Сотрудник активирован');
      const response = await api.get('/api/director/members');
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось изменить статус');
    }
  };

  const seatText = (used, pending, limit) => `${used} активных${pending ? ` + ${pending} приглаш.` : ''} / ${limit ?? '∞'}`;

  if (loading) return <div className="h-full grid place-items-center"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-blue-600">{organization?.name}</p>
          <h1 className="text-3xl font-bold text-slate-900">Сотрудники и франшизы</h1>
          <p className="text-slate-600 mt-1">Приглашения, активные аккаунты и загрузка лимитов в одном месте.</p>
        </div>
        <button onClick={openInvite} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
          <Plus className="w-5 h-5" /> Пригласить сотрудника
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Менеджеры</span><Users className="w-5 h-5 text-blue-600" /></div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{seatText(activeManagers, pendingManagers, organization?.manager_limit)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Супервайзеры</span><ShieldCheck className="w-5 h-5 text-indigo-600" /></div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{seatText(activeSupervisors, pendingSupervisors, organization?.supervisor_limit)}</p>
        </div>
      </div>

      {network.length > 1 && (
        <section>
          <div className="flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-blue-600" /><h2 className="text-xl font-semibold text-slate-900">Франчайзинговая сеть</h2></div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {network.map((item) => (
              <div key={item.id} className={`bg-white border rounded-xl p-5 ${item.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50/30'}`}>
                <div className="flex justify-between gap-3"><h3 className="font-semibold text-slate-900">{item.name}</h3><span className={`text-xs px-2 py-1 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.is_active ? 'Активна' : 'Отключена'}</span></div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <div><p className="text-slate-500">Менеджеры</p><p className="font-semibold">{item.managers} / {item.manager_limit ?? '∞'}</p></div>
                  <div><p className="text-slate-500">Супервайзеры</p><p className="font-semibold">{item.supervisors} / {item.supervisor_limit ?? '∞'}</p></div>
                  <div><p className="text-slate-500">Прошли обучение</p><p className="font-semibold">{item.training_completed} из {item.managers}</p></div>
                  <div><p className="text-slate-500">Сессии за 30 дней</p><p className="font-semibold">{item.sessions_30d}</p></div>
                  <div><p className="text-slate-500">Приглашения</p><p className="font-semibold">{item.pending_invitations}</p></div>
                  <div><p className="text-slate-500">Сценарии</p><p className="font-semibold">{item.scenarios}</p></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200"><h2 className="text-lg font-semibold text-slate-900">Сотрудники</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left px-5 py-3">Сотрудник</th><th className="text-left px-5 py-3">Роль</th><th className="text-left px-5 py-3">Руководитель</th><th className="text-left px-5 py-3">Статус</th><th className="px-5 py-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((member) => {
                const supervisor = members.find((item) => item.id === member.supervisor_id);
                return <tr key={member.id}>
                  <td className="px-5 py-4"><p className="font-medium text-slate-900">{member.first_name} {member.last_name}</p><p className="text-slate-500">{member.email}</p></td>
                  <td className="px-5 py-4">{roleLabels[member.role]}</td>
                  <td className="px-5 py-4 text-slate-600">{supervisor ? `${supervisor.first_name} ${supervisor.last_name}` : '—'}</td>
                  <td className="px-5 py-4"><span className={`px-2 py-1 rounded-full text-xs ${member.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{member.is_active ? 'Активен' : 'Заблокирован'}</span></td>
                  <td className="px-5 py-4 text-right">{member.role !== 'director' && <button onClick={() => toggleMember(member)} className={`inline-flex items-center gap-1 font-medium ${member.is_active ? 'text-red-600' : 'text-green-700'}`}>{member.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}{member.is_active ? 'Блокировать' : 'Активировать'}</button>}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2"><Clock3 className="w-5 h-5 text-amber-600" /><h2 className="text-lg font-semibold text-slate-900">Приглашения</h2></div>
        <div className="divide-y divide-slate-100">
          {invitations.length === 0 ? <p className="p-6 text-slate-500">Приглашений пока нет.</p> : invitations.map((item) => (
            <div key={item.id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div><p className="font-medium text-slate-900">{item.first_name} {item.last_name}</p><p className="text-sm text-slate-500">{item.email} · {roleLabels[item.role]}</p></div>
              <div className="flex items-center gap-3"><span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-full">{statusLabels[item.status]}</span>{item.status !== 'accepted' && <button onClick={() => regenerate(item.id)} className="text-blue-600 font-medium inline-flex items-center gap-1"><RefreshCw className="w-4 h-4" />Новая ссылка</button>}{item.status === 'pending' && <button onClick={() => revoke(item.id)} className="text-red-600 font-medium">Отозвать</button>}</div>
            </div>
          ))}
        </div>
      </section>

      {showInvite && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="p-5 border-b flex items-center justify-between"><h2 className="text-xl font-semibold">Пригласить сотрудника</h2><button onClick={() => setShowInvite(false)}><X className="w-5 h-5" /></button></div>
            {generatedLink ? (
              <div className="p-6">
                <div className="w-12 h-12 bg-green-100 rounded-full grid place-items-center mb-4"><Check className="w-6 h-6 text-green-700" /></div>
                <h3 className="font-semibold text-lg">Ссылка готова</h3><p className="text-slate-600 mt-1">Передайте её сотруднику. Она одноразовая и действует 72 часа.</p>
                <div className="mt-4 flex gap-2"><input readOnly value={generatedLink} className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border rounded-lg text-sm" /><button onClick={() => copyLink()} className="px-4 py-2 bg-blue-600 text-white rounded-lg"><Clipboard className="w-5 h-5" /></button></div>
                <button onClick={() => setShowInvite(false)} className="mt-5 w-full py-3 border border-slate-300 rounded-lg font-medium">Готово</button>
              </div>
            ) : (
              <form onSubmit={createInvitation} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3"><input placeholder="Имя" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="px-3 py-2 border rounded-lg" required /><input placeholder="Фамилия" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="px-3 py-2 border rounded-lg" required /></div>
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required />
                <div><label className="text-sm font-medium text-slate-700">Роль</label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, supervisorId: '' })} className="mt-1 w-full px-3 py-2 border rounded-lg"><option value="manager">Менеджер</option><option value="supervisor">Супервайзер</option></select></div>
                {form.role === 'manager' && <div><label className="text-sm font-medium text-slate-700">Руководитель (необязательно)</label><select value={form.supervisorId} onChange={(e) => setForm({ ...form, supervisorId: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg"><option value="">Без руководителя</option>{supervisors.map((item) => <option key={item.id} value={item.id}>{item.first_name} {item.last_name}</option>)}</select></div>}
                <button disabled={saving} className="w-full py-3 bg-blue-600 disabled:bg-blue-400 text-white rounded-lg font-medium">{saving ? 'Создание...' : 'Создать ссылку'}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationWorkspace;
